import Link from "next/link";
import Image from "next/image";
import { customEvent, getCdnImageSrc } from "goi_common";

import { GOI_PHONE, GOI_PHONE_HREF } from "@/constants/contact";
import { GOI_TRUST } from "@/constants/goiTrust";
import {
  ChevronRightIcon,
  CircleCheckIcon,
} from "@/components/DirectionsIcons";
import { PhoneIcon } from "@/components/PhoneIcon";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";
import type {
  MubinsoPlaceInfo,
  MubinsoTemplate,
} from "@/utils/buildMubinsoPageProps";

interface MubinsoBreadcrumbProps {
  parlorId: number;
  companyName: string;
  sidoName: string;
  sigunguName: string;
  regionHref: string | null;
}

/**
 * 화면 브레드크럼. JSON-LD 만 있고 화면에 없으면 유입된 사람이 상위 표면으로
 * 올라갈 길이 없다. 링크 구조와 BreadcrumbList 스키마가 같은 경로를 가리킨다.
 */
export const MubinsoBreadcrumb = ({
  parlorId,
  companyName,
  sidoName,
  sigunguName,
  regionHref,
}: MubinsoBreadcrumbProps) => {
  const track = (trigger: "btn_crumb_region" | "btn_crumb_parlor") => () =>
    customEvent({
      newGtm: { path: "prlr_mubinso", trigger_and_target: trigger },
    });

  return (
    <nav className="mb-crumb" aria-label="현재 위치">
      <ol>
        <li>
          <Link href="/">고이 장례식장 정보</Link>
        </li>
        {regionHref && (
          <li>
            <Link href={regionHref} onClick={track("btn_crumb_region")}>
              {`${sidoName} ${sigunguName}`}
            </Link>
          </li>
        )}
        <li>
          <Link
            href={`/place/${parlorId}/family/main/`}
            onClick={track("btn_crumb_parlor")}
          >
            {companyName}
          </Link>
        </li>
        <li aria-current="page">무빈소 장례</li>
      </ol>
    </nav>
  );
};

interface MubinsoPlaceLinksProps {
  parlorId: number;
  companyName: string;
  address: string;
  placeInfo: MubinsoPlaceInfo;
  variant?: MubinsoTemplate;
}

/**
 * 이 장례식장의 다른 표면으로 나가는 다리.
 *
 * 무빈소 페이지는 비용과 절차만 다루므로 시설 정보를 찾는 사람은 여기서 막힌다.
 * 무엇을 확인할 수 있는지 먼저 보여준 뒤 상세, 길찾기, 지역으로 보낸다.
 */
export const MubinsoPlaceLinks = ({
  parlorId,
  companyName,
  address,
  placeInfo,
  variant = "A",
}: MubinsoPlaceLinksProps) => {
  const isB = variant === "B";
  /** 상세 페이지 안내 문구 대신 이 장례식장 고유 값을 보여준다 */
  const placeFacts = [
    placeInfo.parkingLabel,
    placeInfo.telephone && `장례식장 대표 전화 ${placeInfo.telephone}`,
  ].filter((fact): fact is string => Boolean(fact));

  const track =
    (trigger: "btn_to_parlor" | "btn_to_directions" | "btn_to_region") => () =>
      customEvent({
        newGtm: {
          path: "prlr_mubinso",
          trigger_and_target: trigger,
          properties: { parlor_id: parlorId },
        },
      });

  return (
    <section className="mb-place" aria-labelledby="mb-place-title">
      <h2 id="mb-place-title" className="mb-place__title">
        {isB
          ? `${companyName} 위치, 주차, 연락처`
          : `${companyName}의 위치와 시설이 궁금하신가요?`}
      </h2>
      <p className="mb-place__address">{address}</p>

      {placeFacts.length > 0 && (
        <ul className="mb-place__checks">
          {placeFacts.map((fact) => (
            <li key={fact}>
              <CircleCheckIcon size={18} />
              <span>{fact}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="mb-place__note">
        {isB
          ? "진행 가능 여부는 장례식장마다 달라 전화 상담으로 바로 알려드립니다."
          : "무빈소로 진행이 가능한지는 장례식장 상황에 따라 달라, 전화로 바로 확인해 드립니다."}
      </p>

      <div className="mb-place__actions">
        <Link
          className="mb-cta mb-cta--light"
          href={`/place/${parlorId}/family/main/`}
          onClick={track("btn_to_parlor")}
        >
          빈소, 시설 정보 보기
          <ChevronRightIcon size={16} />
        </Link>
        <MubinsoPhoneCta parlorId={parlorId} slot="place" />
      </div>
    </section>
  );
};

interface MubinsoPhoneCtaProps {
  parlorId: number;
  /** 같은 문구의 버튼이 여러 군데 있어 어디서 눌렀는지 구분한다 */
  slot: "cost" | "place" | "bottom";
}

export const MubinsoPhoneCta = ({ parlorId, slot }: MubinsoPhoneCtaProps) => {
  const { openParlorInquiryModal } = useParlorInquiryModal("prlr_mubinso");

  return (
    <a
      className="mb-cta mb-cta--phone"
      href={GOI_PHONE_HREF}
      aria-label={`24시간 장례 무료 상담 ${GOI_PHONE}`}
      onClick={(event) => {
        event.preventDefault();
        customEvent({
          newGtm: {
            path: "prlr_mubinso",
            trigger_and_target:
              slot === "cost" ? "btn_call_cost" : "btn_call_bottom",
            properties: { parlor_id: parlorId },
          },
        });
        void openParlorInquiryModal();
      }}
    >
      <PhoneIcon size={18} />
      24시간 장례 무료 상담
    </a>
  );
};

/**
 * 고이 가이드북 콘텐츠.
 *
 * 이 페이지는 특정 장례식장의 무빈소 비용과 절차를 다루고, "무빈소가 무엇인지"
 * 자체를 길게 설명하지는 않는다. 그 설명은 가이드북이 이미 갖고 있으므로
 * 같은 내용을 1,023개 페이지에 복붙하지 않고 원문으로 보낸다.
 */
const GUIDES = [
  {
    slug: "before/mubinso",
    title: "무빈소 장례란? 비용과 주의할 점",
    desc: "빈소를 차리지 않아 이용료가 들지 않는 이유와, 2일장으로 진행되는 배경을 정리했습니다.",
  },
  {
    slug: "ongoing/3",
    title: "무빈소 장례의 모든 것",
    desc: "접수부터 안치, 입관, 발인까지 무빈소 장례 절차를 3분 안에 알려드립니다.",
  },
  {
    slug: "before/funeral-expense",
    title: "장례식 비용 3분 안에 정리하기",
    desc: "상조 회사 비용, 장례식장 비용, 장지 비용으로 나눠 장례비의 구조를 설명합니다.",
  },
  {
    slug: "before/funeral-cost-save",
    title: "장례 비용 줄이는 법 핵심 요약",
    desc: "어디서 얼마를 줄일 수 있는지, 실제로 아낄 수 있는 항목만 추렸습니다.",
  },
];

interface MubinsoGuideCardsProps {
  parlorId: number;
}

export const MubinsoGuideCards = ({ parlorId }: MubinsoGuideCardsProps) => (
  <section className="mb-guides" aria-labelledby="mb-guides-title">
    <h2 id="mb-guides-title" className="mb-section__title">
      무빈소 장례, 더 알아보기
    </h2>
    <ul className="mb-guides__grid">
      {GUIDES.map((guide) => (
        <li key={guide.slug}>
          <a
            className="mb-guide"
            href={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/guidebook/tip/${guide.slug}/?utm_source=parlor_goi&utm_campaign=mubinso&utm_content=guide`}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_mubinso",
                  trigger_and_target: "btn_guide",
                  properties: { parlor_id: parlorId, guide: guide.slug },
                },
              })
            }
          >
            {/* 썸네일 규칙: images/guidebook/tip/thumbnail/{category}/{index}.png */}
            <span className="mb-guide__thumb">
              <Image
                src={getCdnImageSrc(
                  `images/guidebook/tip/thumbnail/${guide.slug}.png`
                )}
                alt=""
                width={600}
                height={400}
                unoptimized
              />
            </span>
            <span className="mb-guide__body">
              <span className="mb-guide__label">가이드북</span>
              <span className="mb-guide__title">{guide.title}</span>
              <span className="mb-guide__desc">{guide.desc}</span>
              <span className="mb-guide__more">
                읽어보기
                <ChevronRightIcon size={14} />
              </span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * 이 페이지를 누가 썼는지 밝히는 블록.
 *
 * 장례식장별 비용을 추산해 공개하는 페이지라 작성 주체가 드러나야 신뢰가 생긴다.
 * E-E-A-T 관점에서도 publisher 정보가 본문에 있는 편이 낫다.
 */
export const MubinsoPublisher = ({
  parlorId,
  variant = "A",
}: {
  parlorId: number;
  variant?: MubinsoTemplate;
}) => (
  <section className="mb-publisher" aria-labelledby="mb-publisher-title">
    <h2 id="mb-publisher-title" className="mb-publisher__title">
      {variant === "B"
        ? "정보 제공: 고이장례연구소"
        : "이 정보는 고이장례연구소가 안내드립니다"}
    </h2>
    {variant === "B" ? (
      <p className="mb-publisher__desc">
        e하늘 장사정보시스템 공공 데이터로 장례식장 이용료를 정리하고, 후불제
        상조로 장례를 진행합니다. 쓰지 않은 품목은 공제하고, 현장에서 추가
        결제를 유도하면 전액 환불합니다.
      </p>
    ) : (
      <p className="mb-publisher__desc">
        고이장례연구소는 보건복지부 e하늘 장사정보시스템의 공공 데이터를
        기준으로 전국 장례식장의 시설 이용료를 정리하고, 후불제 상조로 장례를
        도와드리는 회사입니다.
        <br />
        {`장례, 상조 상담 ${GOI_TRUST.consultValue}과 후기 평균 ${GOI_TRUST.scoreValue}점을 쌓아왔고, 사용하지 않은 품목은 공제하며 현장에서 추가 결제를 유도할 경우 100% 환불하는 정책을 시행하고 있습니다.`}
      </p>
    )}
    <a
      className="mb-publisher__link"
      href={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/funeral-prepare/company-introduction/?utm_source=parlor_goi&utm_campaign=mubinso&utm_content=company`}
      target="_blank"
      rel="noreferrer noopener"
      onClick={() =>
        customEvent({
          newGtm: {
            path: "prlr_mubinso",
            trigger_and_target: "btn_to_company",
            properties: { parlor_id: parlorId },
          },
        })
      }
    >
      고이장례연구소 자세히 알아보기
      <ChevronRightIcon size={14} />
    </a>
  </section>
);
